import React, { useEffect, useState } from 'react';
import useScreenWidth from '../../../../store/useScreenWidth';
import { TicketsByStatuses } from '../../../../models/Dashboard';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';

interface GaugeProps {
    data: TicketsByStatuses[];
}

const GaugeComponent: React.FC<GaugeProps> = ({ data }) => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();
    const isSmallScreen = useScreenWidth();
    const radius = 75;
    const totalSegments = isSmallScreen ? 30 : 50;
    const dashLength = 20;

    const totalValue = data.reduce((acc, status) => acc + (status.ticketCount || 0), 0);

    const [animationProgress, setAnimationProgress] = useState(0);

    useEffect(() => {
        setAnimationProgress(0);
        const interval = setInterval(() => {
            setAnimationProgress((prev) => Math.min(prev + 10, 360)); // Rrit animacionin gradualisht
        }, 20);

        return () => clearInterval(interval);
    }, [data]);

    const calculateRanges = () => {
        let startDegree = 0;
        return data.map(item => {
            const endDegree = startDegree + (item.ticketCount / totalValue) * 360;
            const color = item.badgeColor || '#8D97A0';
            const range = { startDegree, endDegree, color };
            startDegree = endDegree;
            return range;
        });
    };

    const ranges = calculateRanges();

    const segments = Array.from({ length: totalSegments }).map((_, index) => {
        const angle = (index * 360) / totalSegments;
        const range = ranges.find(r => angle >= r.startDegree && angle < r.endDegree);
        return {
            index,
            color: totalValue === 0 ? '#02B2AF' : range?.color || 'transparent',
            isVisible: angle <= animationProgress // Kontrollo nëse segmenti duhet të jetë i dukshëm
        };
    });

    const colorText = darkMode ? "white" : "rgba(50, 71, 92, 0.6)";

    return (
        <svg width={radius * 2} height={radius * 2} viewBox="0 0 160 160">
            <g transform={`translate(${radius}, ${radius})`}>
                {segments.map((segment) => {
                    const rotate = (segment.index * 360) / totalSegments;
                    return (
                        <line
                            key={segment.index}
                            x1="0"
                            x2="0"
                            y1={-radius}
                            strokeWidth="6"
                            y2={-radius + dashLength}
                            transform={`rotate(${rotate})`}
                            style={{ transition: 'stroke 0.3s ease-in-out' }}
                            stroke={segment.isVisible ? segment.color : 'transparent'}
                        />
                    );
                })}
                <text
                    x="0"
                    y="0"
                    fontSize="30"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={darkMode ? 'white' : 'black'}
                >
                    <tspan x="0" dy="-10" opacity={darkMode ? 1 : 0.7} fontWeight={600}>
                        {totalValue}
                    </tspan>
                    <tspan
                        x="0"
                        dy="30"
                        fontSize={18}
                        fill={colorText}
                        fontWeight={600}
                        opacity={darkMode ? 1 : 0.4}
                    >
                        {totalValue === 1 ? language['Ticket2'] : language['Ticket']}
                    </tspan>
                </text>
            </g>
        </svg>
    );
};

export default GaugeComponent;
